import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "./useAuth";

export interface ActiveSpace {
  id: string;
  name: string;
}

export interface ActiveSpaceResult {
  space: ActiveSpace | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
}

// Resolution chain — must stay identical to useFeedEngine's inline fallback
// so that the engine, the capture screen, the children query, and the feed
// engine all agree on which space is "active" at any moment.
//
//   1. profiles.active_space_id (explicit — set by accept-coparent-invite;
//                                or by a future space-picker UI)
//   2. the user's owned space (auto-created at signup)
//   3. the first active space_members row (fallback for co-parents whose
//      active_space_id wasn't backfilled)
//
// Exported so non-hook callers (e.g. async helpers in useCollectionTemplates)
// can share the exact same resolution logic without re-implementing it.
// Seeds the TanStack Query cache so subsequent hook renders are instant.
export async function resolveActiveSpace(userId: string): Promise<ActiveSpace | null> {
  const [profileRes, ownedRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("active_space_id")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("spaces")
      .select("id, name")
      .eq("owner_id", userId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle(),
  ]);

  const profileActiveId =
    (profileRes.data as { active_space_id: string | null } | null)
      ?.active_space_id ?? null;
  const ownedSpace = ownedRes.data as ActiveSpace | null;

  let result: ActiveSpace | null = null;

  if (profileActiveId) {
    if (ownedSpace && ownedSpace.id === profileActiveId) {
      result = ownedSpace;
    } else {
      const { data } = await supabase
        .from("spaces")
        .select("id, name")
        .eq("id", profileActiveId)
        .is("deleted_at", null)
        .maybeSingle();
      result = (data as ActiveSpace | null) ?? ownedSpace ?? null;
    }
  } else if (ownedSpace) {
    result = ownedSpace;
  } else {
    const { data: membership } = await supabase
      .from("space_members")
      .select("space_id, spaces!space_id ( id, name )")
      .eq("user_id", userId)
      .eq("status", "active")
      .neq("role", "owner")
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    result =
      (membership as unknown as {
        spaces: { id: string; name: string } | null;
      } | null)?.spaces ?? null;

    // Back-fill active_space_id so subsequent callers skip this chain.
    if (result) {
      void supabase
        .from("profiles")
        .update({ active_space_id: result.id })
        .eq("id", userId);
    }
  }

  queryClient.setQueryData(["active-space", userId], result);
  return result;
}

export function useActiveSpace(): ActiveSpaceResult {
  const { user } = useAuth();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["active-space", userId],
    enabled: !!userId,
    queryFn: () => resolveActiveSpace(userId!),
  });

  return {
    space: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
